import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tooltip, Typography, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';

import color from 'src/assets/_util.scss';
import authSelectors from 'src/redux/auth/auth-selectors';
import { FeatureFlags } from 'src/types/core';
import { getFlag } from 'src/util/user';
import { useCatalogPageContext } from './CatalogPageProvider';

export default function TabNavigation({
  activeTab,
  setActiveTab,
}: {
  activeTab: number;
  setActiveTab: (tab: number) => void;
}) {
  const { t } = useTranslation();
  const [tabWidth, setTabWidth] = React.useState<number | undefined>();
  const tabLabelRef = React.useRef<HTMLSpanElement>(null);
  const theme = useTheme();
  const { iAmAdminOrOwner, isLoading } = useCatalogPageContext();

  const user = useSelector(authSelectors.getUser);
  const isProjectsPlatform = getFlag(user, FeatureFlags.projects_platform);

  const handleTabSelect = (event: React.SyntheticEvent, tabIdx: number) => {
    setTabWidth(event.currentTarget.children.item(0)?.clientWidth);
    setActiveTab(tabIdx);
  };

  React.useEffect(() => {
    if (tabLabelRef.current) {
      setTabWidth(tabLabelRef.current.offsetWidth);
    }
  }, []);

  return (
    <Tabs
      onChange={handleTabSelect}
      value={activeTab}
      indicatorColor="primary"
      textColor="primary"
      TabIndicatorProps={{
        children: <span className="MuiTabs-indicatorSpan" />,
        sx: {
          display: 'flex',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          '& .MuiTabs-indicatorSpan': {
            width: `${tabWidth}px !important`,
            backgroundColor: theme.palette.primary.main,
          },
        },
      }}
      sx={{
        backgroundColor: '#ffffff',
        borderBottom: `1px solid ${color.gray300}`,
        paddingLeft: '10px',
      }}
    >
      <Tab
        component="button"
        disableRipple
        data-testid="highlights-nav-tab"
        label={
          <Typography variant="body2">
            {t('insights.navigation_highlights')}
          </Typography>
        }
      />

      <Tab
        component="button"
        disableRipple
        data-testid="conversations-nav-tab"
        label={
          <Typography variant="body2">
            {!isProjectsPlatform
              ? t('insights.navigation_conversations')
              : t('insights.navigation_demographics')}
          </Typography>
        }
      />

      {!isProjectsPlatform && (
        <Tooltip
          placement="bottom"
          title={t('insights.sensemakers_disable_tooltip')}
          disableHoverListener={iAmAdminOrOwner}
        >
          <Tab
            component="button"
            disableRipple
            disabled={isLoading || !iAmAdminOrOwner}
            data-testid="sensemakers-nav-tab"
            label={
              <Typography variant="body2">
                {t('insights.sensemakers')}
              </Typography>
            }
          />
        </Tooltip>
      )}

      {!isProjectsPlatform && (
        <Tab
          component="button"
          disableRipple
          data-testid="overview-nav-tab"
          label={
            <Typography ref={tabLabelRef} variant="body2">
              {t('insights.navigation_overview')}
            </Typography>
          }
          sx={{ px: 3 }}
        />
      )}
    </Tabs>
  );
}

export function useTabNavigation(defaultValue?: number) {
  const [activeTab, setActiveTab] = React.useState(defaultValue ?? 0);
  return { activeTab, setActiveTab };
}
